import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Form } from 'react-final-form';
import { YesNo } from 'models/Fields';
import { IM_EXTRACTION_STATUS } from 'models/Listing';
import { Button } from 'ncoded-component-library';
import ArrowrightIcon from 'icons/Arrowright.icon';
import SparklesIcon from 'icons/Sparkles.icon';
import HintMessage from 'components/HintMessage';
import ResolveHeader from 'components/ResolveHeader';
import {
  getCreateListingRoute,
  getFileNameWithoutExtension,
  getSellingPortfolio,
} from 'utils';
import { LISTING_IM } from 'hooks/useUploadIm';
import { LISTING_TYPE_FLOW } from 'hooks/useDraftListingOptions';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DealRoomContext from '../../providers/DealRoom/DealRoom.context';
import DraftListingFields from 'components/DraftListingFields';

import './MarketYourListing.styles.scss';
import './MarketYourListing.styles.responsive.scss';

const MarketYourListing: FC = () => {
  const { dealRoom } = useContext(DealRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const { id: dealRoomId } = useParams();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { imExtractionStatus, imFileName } = dealRoom?.listing || {};

  // Surfaces a previous AI attempt on this deal. The form below stays usable either
  // way, so a failed run can be retried by uploading the document again.
  const extractionMessage = useMemo(() => {
    const documentName = getFileNameWithoutExtension(
      imFileName || dealRoom?.listingIM?.name,
    );

    if (imExtractionStatus === IM_EXTRACTION_STATUS.IN_PROGRESS)
      return {
        type: 'info' as const,
        icon: <SparklesIcon />,
        message: documentName
          ? t('aiIsDraftingYourListing', { documentName })
          : t('aiIsDraftingYourListingNoDocument'),
      };

    if (imExtractionStatus === IM_EXTRACTION_STATUS.FAILED)
      return {
        type: 'error' as const,
        message: documentName
          ? t('aiCouldNotDraftYourListing', { documentName })
          : t('aiCouldNotDraftYourListingNoDocument'),
      };

    return null;
  }, [imExtractionStatus, imFileName, dealRoom?.listingIM?.name, t]);

  const onSubmit = useCallback(
    async ({ draftListing, [LISTING_TYPE_FLOW]: listingTypeFlow }) => {
      const {
        _id,
        listing: { _id: listingId, type },
      } = dealRoom;

      if (draftListing !== YesNo.Yes) {
        navigate(getCreateListingRoute(_id, listingId, type), {
          replace: true,
        });
        return;
      }

      try {
        await api.dealRoom.triggerImExtraction(_id, listingTypeFlow);

        navigate(getSellingPortfolio(_id), { replace: true });
      } catch (error) {
        popServerError(error);
      }
    },
    [dealRoom, navigate, popServerError],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        [LISTING_IM]: dealRoom?.listingIM,
        draftListing: dealRoom?.listingIM ? YesNo.Yes : null,
        [LISTING_TYPE_FLOW]: dealRoom?.listing?.type,
      }}
      render={({ submitting, invalid, values, dirtyFields, handleSubmit }) => {
        // A draft is already running - submitting again would start a second one.
        const isDrafting =
          imExtractionStatus === IM_EXTRACTION_STATUS.IN_PROGRESS;

        // A failed run is only worth retrying with a different document. Choosing to
        // draft the listing yourself stays available either way.
        const isRetryingWithSameDocument =
          imExtractionStatus === IM_EXTRACTION_STATUS.FAILED &&
          values.draftListing === YesNo.Yes &&
          !dirtyFields[LISTING_IM];

        return (
          <form onSubmit={handleSubmit} className="market-your-listing">
            <ResolveHeader />

            <main>
              <div>
                <h1>{t('marketYourDealWithListing')}</h1>

                {extractionMessage && <HintMessage {...extractionMessage} />}

                <DraftListingFields
                  dealRoomId={dealRoomId}
                  dealName={dealRoom?.name}
                  fieldName="draftListing"
                />

                <div className="actions">
                  <Button
                    disabled={
                      invalid ||
                      submitting ||
                      isDrafting ||
                      isRetryingWithSameDocument
                    }
                    icon={ArrowrightIcon}
                    iconPosition="right"
                    type="submit"
                  >
                    {t('next')}
                  </Button>
                </div>
              </div>
            </main>
          </form>
        );
      }}
    />
  );
};

export default MarketYourListing;
